/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

type ModalValues = {
  onDismiss?: () => void;
  content: ReactNode | null;
};

type ModalStateValue = {
  isActive: boolean;
} & ModalValues;

type ModalContextValue = {
  dismissModal: () => void;
  setModal: (modal: ModalValues) => void;
};

type ModalProviderProps = {
  children: ReactNode;
};

const initialState: ModalStateValue = {
  isActive: false,
  content: null,
  onDismiss: undefined,
};

const ModalContext = createContext<ModalContextValue>({} as ModalContextValue);

export const ModalProvider = (props: ModalProviderProps) => {
  const { children } = props;

  const [state, setModalState] = useState<ModalStateValue>(initialState);
  const { content, isActive, onDismiss } = state;

  const setModal = useCallback(
    ({ content, onDismiss }: ModalValues) => {
      setModalState({
        isActive: true,
        content,
        onDismiss,
      });
    },
    [setModalState],
  );

  const dismissModal = useCallback(() => {
    onDismiss?.();
    setModalState({ ...initialState });
  }, [setModalState, onDismiss]);

  return (
    <ModalContext.Provider value={{ dismissModal, setModal }}>
      {children}
      {isActive && content ? content : null}
    </ModalContext.Provider>
  );
};

export const useModal = (): ModalContextValue => {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error("Component beyond ModalContext");
  }

  return context;
};
